import { useState } from 'react';
import type { WeekType } from '@functieloonhuis/shared';
import { useEmployeeSchedule, useCurrentWeekType, useUpsertSchedule, useDeleteSchedule } from '../hooks/useWorkSchedules';
import { hasWeekData, getDefaultWeek } from '../utils/weekUtils';
import { WeekToggle } from './WeekToggle';
import { WeekScheduleGrid } from './WeekScheduleGrid';
import { ScheduleForm } from './ScheduleForm';
import { useToast } from '../../../components/Toast';

interface EmployeeScheduleTabProps {
  employeeId: number;
  canEdit: boolean;
}

export function EmployeeScheduleTab({ employeeId, canEdit }: EmployeeScheduleTabProps) {
  const { data: scheduleData, isLoading: scheduleLoading } = useEmployeeSchedule(employeeId);
  const { data: weekData } = useCurrentWeekType();
  const upsertMutation = useUpsertSchedule(employeeId);
  const deleteMutation = useDeleteSchedule(employeeId);
  const { showToast } = useToast();

  const schedule = scheduleData?.data;
  const currentWeekType = weekData?.data?.currentWeekType;
  const hasWeekB = hasWeekData(schedule?.weekB ?? []);

  const [selectedWeek, setSelectedWeek] = useState<WeekType | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // Determine which week to display
  const effectiveWeek = selectedWeek ?? getDefaultWeek(currentWeekType, hasWeekB);
  const displayEntries = effectiveWeek === 'A' ? (schedule?.weekA ?? []) : (schedule?.weekB ?? []);
  const hasAnyData = hasWeekData(schedule?.weekA ?? []) || hasWeekB;

  if (scheduleLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
        <span className="ml-3 text-sm text-gray-500">Werkrooster laden...</span>
      </div>
    );
  }

  // Empty state — allow creating directly in edit mode
  if (!hasAnyData && !isEditing) {
    return (
      <div className="text-center py-12">
        <svg className="mx-auto h-12 w-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <p className="mt-4 text-gray-500">Er is nog geen werkrooster ingevuld</p>
        {canEdit ? (
          <button
            onClick={() => setIsEditing(true)}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Rooster aanmaken
          </button>
        ) : (
          <p className="mt-2 text-sm text-gray-400">
            Neem contact op met je manager of HR
          </p>
        )}
      </div>
    );
  }

  // Edit mode
  if (isEditing) {
    return (
      <ScheduleForm
        initialWeekA={schedule?.weekA}
        initialWeekB={schedule?.weekB}
        isSaving={upsertMutation.isPending}
        onSave={(entries) => {
          upsertMutation.mutate({ entries }, {
            onSuccess: () => {
              showToast('Werkrooster opgeslagen', 'success');
              setIsEditing(false);
            },
            onError: () => {
              showToast('Opslaan mislukt. Probeer het opnieuw.', 'error');
            },
          });
        }}
        onCancel={() => setIsEditing(false)}
        onDelete={hasAnyData ? () => {
          deleteMutation.mutate(undefined, {
            onSuccess: () => {
              showToast('Werkrooster verwijderd', 'success');
              setIsEditing(false);
            },
            onError: () => {
              showToast('Verwijderen mislukt. Probeer het opnieuw.', 'error');
            },
          });
        } : undefined}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with edit button */}
      <div className="flex items-center justify-between">
        {/* Week toggle — only show if Week B exists */}
        {hasWeekB ? (
          <WeekToggle
            selectedWeek={effectiveWeek}
            onWeekChange={setSelectedWeek}
            currentWeekType={currentWeekType}
            hasWeekB={hasWeekB}
          />
        ) : (
          <div />
        )}

        <div className="flex items-center gap-3">
          {currentWeekType && hasWeekB && (
            <span className="text-xs text-gray-400">
              Huidige week: {currentWeekType}
            </span>
          )}
          {canEdit && (
            <button
              onClick={() => setIsEditing(true)}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <svg className="mr-1.5 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Bewerken
            </button>
          )}
        </div>
      </div>

      {/* Schedule grid */}
      <WeekScheduleGrid entries={displayEntries} />
    </div>
  );
}
